import { forwardRef, ForwardRefExoticComponent, RefAttributes } from "react";
import clsx from "clsx";
import { Icon, IconProps } from "@tabler/icons-react";

import { Check } from "@chef/icons/small";

import SubText from "../../internals/SubText";

type TablerIconComponent = ForwardRefExoticComponent<
  IconProps & RefAttributes<Icon>
>;

type IconSvgComponent = (
  props: React.SVGProps<SVGSVGElement>,
) => React.ReactElement | null;

type IconComponent = TablerIconComponent | IconSvgComponent;

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  Icon?: IconComponent;
  IconRight?: IconComponent;
  placeholder?: string;
  name?: string;
  value?: string;
  maxLength?: number;
  label?: string;
  error?: string;
  warning?: string;
  helperText?: string;
  valid?: boolean;
  showLengthCounter?: boolean;
  id?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      Icon,
      IconRight,
      placeholder,
      disabled,
      value = "",
      onChange,
      name,
      type = "text",
      maxLength,
      label = "",
      error = "",
      warning = "",
      helperText = "",
      valid = false,
      className,
      showLengthCounter = true,
      id,
      ...props
    },
    ref,
  ) => {
    const hasValue = value !== "" && value !== undefined;

    return (
      <div className={className} id={id}>
        <div
          {...props}
          className={clsx(
            "flex items-center border-1.5 border-black rounded-lg h-12 relative a11y-focus-within:focus-ring focus-within:!border-black",
            disabled ? "!border-grey-1 bg-background" : "bg-white",
            valid && "!border-information",
            error && "!border-error",
          )}
        >
          {Icon && <Icon className="ml-4" />}
          <input
            {...props}
            id={`${id}-input`}
            ref={ref}
            name={name}
            type={type}
            placeholder={placeholder || label}
            disabled={disabled}
            value={value}
            onChange={onChange}
            maxLength={maxLength && maxLength > 0 ? maxLength : undefined}
            className={clsx(
              "flex-grow w-full h-full px-4 focus:outline-none rounded-lg",
              disabled ? "cursor-not-allowed bg-background" : "bg-transparent",
            )}
          />
          {valid && !Icon && !error && (
            <Check className="mr-4 text-information" />
          )}
          {IconRight && (
            <IconRight
              className={clsx("mr-4 min-w-min", disabled && "text-grey-1")}
            />
          )}
          {label && !Icon && (
            <label
              className={clsx(
                "pointer-events-none opacity-0 absolute px-2 left-2 transition-all ease-in-out duration-75",
                hasValue
                  ? "!opacity-100 -top-2.5 bg-white text-xs text-black rounded"
                  : "top-2.5",
              )}
              htmlFor={name}
            >
              {label}
            </label>
          )}
        </div>
        <SubText
          error={error}
          warning={warning}
          helperText={helperText}
          maxLength={maxLength}
          value={value}
          showLengthCounter={showLengthCounter}
          htmlFor={name}
        />
      </div>
    );
  },
);
